import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as ChevronDownIcon } from '../../../../../../../../assets/icons/chevron-down.svg';
import CustomButton from '../../../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import ExternalBenefitsDocs from '../../../../../../../../shared/components/ExternalBenefits/ExternalBenefitsDocs/ExternalBenefitsDocs';
import Badge from '../../../../../../../../shared/components/Globals/Badge/Badge';
import { CLIENT_PLAN_STATUS_BADGE } from '../../../../../../../../shared/constants/clients';
import {
  BENEFITS_DOCUMENTS_SOURCE,
  CLICK_ACTION,
  VIEW_PLAN_DETAILS
} from '../../../../../../../../shared/constants/tagManager';
import type { IClientPlan } from '../../../../../../../../shared/interfaces/clients';
import type { IQuote } from '../../../../../../../../shared/interfaces/quotes';
import TagManagerService from '../../../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_CLIENTS } from '../../../../../../../../shared/translations/clients';
import { TRANSLATIONS_EXTERNAL_BENEFITS } from '../../../../../../../../shared/translations/commons';
import { ClientPlanStatus } from '../../../../../../../../shared/types/clients';
import type { BadgeVariant } from '../../../../../../../../shared/types/globals';
import {
  deriveAppOnlyPlanDates,
  deriveClientPlanDates,
  getAppOnlyPlanSummary,
  getClientPlanSummary,
  getEnrollmentDaysLeft,
  resolveAppOnlyPlanStatus,
  resolveClientPlanStatus,
  type IClientPlanDates
} from '../../../../../../../../shared/utils/clients/clientPlans';
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '../../../../../../../../shared/utils/currency/currency';
import { formatDateDisplay, parseDateISO } from '../../../../../../../../shared/utils/dates/dates';
import PlanDetailsModal from '../../../../components/PlanDetailsModal/PlanDetailsModal';

import './ActivePlanCard.scss';

const PLANS = TRANSLATIONS_CLIENTS.profile.plans;

interface IActivePlanCardProps {
  plan: IClientPlan;
  enrollmentWindowDays: number;
  appOnly?: boolean;
  hasActivationError?: boolean;
  quote?: IQuote;
  brokerId?: string;
  onQuoteUpdate?: (updated: IQuote) => void;
}

interface IDetailRow {
  label: string;
  value: string | null;
}

const buildDetailRows = (
  appOnly: boolean,
  plan: IClientPlan,
  dates: IClientPlanDates,
  isAwaiting: boolean,
  formatDate: (iso: string | null) => string | null,
  t: (key: string) => string,
  monthlyRateValue: string | null
): IDetailRow[] => {
  const segmentRow: IDetailRow | null =
    plan.segment !== undefined
      ? { label: t(PLANS.details.segment), value: plan.segment || t(PLANS.details.allSegments) }
      : null;

  const rows: (IDetailRow | null)[] = appOnly
    ? [
        { label: t(PLANS.details.plan), value: plan.planName || null },
        { label: t(PLANS.details.monthlyRate), value: monthlyRateValue },
        { label: t(PLANS.details.startDate), value: formatDate(plan.startDate) },
        { label: t(PLANS.details.renewalDate), value: formatDate(dates.renewalDate) },
        segmentRow
      ]
    : [
        isAwaiting && plan.startDate
          ? { label: t(PLANS.details.enrollmentOpens), value: formatDate(plan.startDate) }
          : null,
        { label: t(PLANS.details.effectiveDate), value: formatDate(dates.effectiveDate) },
        { label: t(PLANS.details.renewalDate), value: formatDate(dates.renewalDate) },
        segmentRow
      ];

  return rows.filter((row): row is IDetailRow => row !== null);
};

const ActivePlanCard = ({
  plan,
  enrollmentWindowDays,
  appOnly = false,
  hasActivationError = false,
  quote,
  brokerId,
  onQuoteUpdate
}: IActivePlanCardProps) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const status = appOnly
    ? resolveAppOnlyPlanStatus(plan.startDate)
    : resolveClientPlanStatus(plan.startDate, enrollmentWindowDays);
  const badge: { variant: BadgeVariant; labelKey: string } = hasActivationError
    ? { variant: 'lost', labelKey: TRANSLATIONS_CLIENTS.clientsList.badges.activationError }
    : CLIENT_PLAN_STATUS_BADGE[status];
  const isAwaiting = status === ClientPlanStatus.AwaitingStart;
  const dates = appOnly
    ? deriveAppOnlyPlanDates(plan.startDate)
    : deriveClientPlanDates(plan.startDate, enrollmentWindowDays);
  const daysLeft = appOnly ? 0 : getEnrollmentDaysLeft(plan.startDate, enrollmentWindowDays);

  const formatDate = (iso: string | null): string | null =>
    iso ? formatDateDisplay(parseDateISO(iso), i18n.language) : null;

  const title = plan.name || t(PLANS.details.empty);

  const handleViewDetails = () => {
    TagManagerService.pushEvent(CLICK_ACTION, VIEW_PLAN_DETAILS);
    setDetailsOpen(true);
  };

  const benefitsPending = appOnly && !!quote;

  const summaryParts = (
    appOnly
      ? getAppOnlyPlanSummary(status, plan.startDate, dates.renewalDate, formatDate)
      : getClientPlanSummary(status, dates, plan.startDate, daysLeft, formatDate)
  ).map((item) => t(item.key, item.params));

  if (benefitsPending) {
    summaryParts.push(t(TRANSLATIONS_EXTERNAL_BENEFITS.block.pending));
  }

  const summaryText = hasActivationError ? t(PLANS.summary.activationFailed) : summaryParts.join(' · ');

  const monthlyRateValue =
    plan.monthlyRate != null
      ? t(PLANS.details.perEmployeeAmount, {
          amount: formatCurrency(plan.monthlyRate, plan.currencyCode || DEFAULT_CURRENCY_CODE, i18n.language)
        })
      : null;

  const detailRows = buildDetailRows(appOnly, plan, dates, isAwaiting, formatDate, t, monthlyRateValue);

  const showTimeline = !appOnly && status === ClientPlanStatus.Enrolling && !!plan.startDate;
  const timelineSteps = [
    { label: t(PLANS.enrollment.timeline.opens), value: formatDate(plan.startDate), state: 'past' },
    { label: t(PLANS.enrollment.timeline.closes), value: formatDate(dates.enrollmentEnd), state: 'current' },
    { label: t(PLANS.enrollment.timeline.coverageStarts), value: formatDate(dates.coverageStart), state: 'upcoming' }
  ];

  return (
    <div className={`active-plan-card${expanded ? ' active-plan-card--open' : ''}`}>
      <button
        type="button"
        className={`active-plan-card__header${expanded ? ' active-plan-card__header--open' : ''}`}
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <span className="active-plan-card__head">
          <span className="active-plan-card__name">{title}</span>
          <Badge variant={badge.variant}>{t(badge.labelKey)}</Badge>
        </span>
        <span className="active-plan-card__summary">{summaryText}</span>
        <span
          className={`active-plan-card__chevron${expanded ? ' active-plan-card__chevron--open' : ''}`}
          aria-hidden="true"
        >
          <ChevronDownIcon />
        </span>
      </button>

      {expanded && (
        <div className="active-plan-card__body">
          {showTimeline && (
            <div className="active-plan-card__enrollment">
              <div className="active-plan-card__enrollment-title">{t(PLANS.enrollment.title)}</div>
              <div className="active-plan-card__metrics">
                <div className="active-plan-card__metric">
                  <span className="active-plan-card__metric-value">{daysLeft}</span>
                  <span className="active-plan-card__metric-label">{t(PLANS.enrollment.daysLeft)}</span>
                </div>
              </div>
              <ol className="active-plan-card__timeline">
                {timelineSteps.map((step) => (
                  <li key={step.label} className={`active-plan-card__step active-plan-card__step--${step.state}`}>
                    <span className="active-plan-card__step-marker" aria-hidden="true" />
                    <span className="active-plan-card__step-label">{step.label}</span>
                    <span className="active-plan-card__step-date">{step.value ?? t(PLANS.details.empty)}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <dl className="active-plan-card__rows">
            {detailRows.map((row) => (
              <div key={row.label} className="active-plan-card__row">
                <dt className="active-plan-card__row-label">{row.label}</dt>
                <dd className={`active-plan-card__row-value${row.value ? '' : ' active-plan-card__row-value--empty'}`}>
                  {row.value ?? t(PLANS.details.empty)}
                </dd>
              </div>
            ))}
          </dl>

          {appOnly && quote && brokerId && quote.prospectId != null && (
            <ExternalBenefitsDocs
              layout="record"
              quote={quote}
              brokerId={brokerId}
              prospectId={quote.prospectId}
              source={BENEFITS_DOCUMENTS_SOURCE.activePlan}
              onSubmitted={(updated) => onQuoteUpdate?.(updated)}
            />
          )}

          {!appOnly && (
            <div className="active-plan-card__footer">
              <CustomButton variant="default" size="s" onClick={handleViewDetails}>
                {t(PLANS.viewDetails)}
              </CustomButton>
            </div>
          )}
        </div>
      )}

      {!appOnly && (
        <PlanDetailsModal
          open={detailsOpen}
          plan={plan}
          onClose={() => setDetailsOpen(false)}
          statusBadge={badge}
          effectiveDate={formatDate(dates.effectiveDate)}
          renewalDate={formatDate(dates.renewalDate)}
        />
      )}
    </div>
  );
};

export default ActivePlanCard;
